/* @jsx h */
import { computed, defineComponent, h, ref } from "vue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { RightSheet, useLocalization } from "@lemmonhq/uikit";
import type {
  MembershipLinkedProfile,
  MembershipMember,
  MembershipProgramWithStats,
} from "@lemmonhq/types";
import { useTrpc } from "@app/vue/providers/index";
import { AddMemberModal, type AddMemberInput } from "./AddMemberModal";

export type MembershipMembersSheetProps = {
  program: MembershipProgramWithStats;
  scope?: "shop" | "group";
  canManage?: boolean;
  onClose: () => void;
};

const initials = (member: { name?: string | null; email?: string | null }) => {
  const source = (member.name || member.email || "?").trim();
  return source.slice(0, 1).toUpperCase();
};

export const MembershipMembersSheet =
  defineComponent<MembershipMembersSheetProps>({
    name: "MembershipMembersSheet",
    props: ["program", "scope", "canManage", "onClose"],
    setup(props) {
      const { t } = useLocalization();
      const trpc = useTrpc();
      const queryClient = useQueryClient();
      const isGroup = props.scope === "group";
      const canManage = props.canManage !== false;

      const search = ref("");
      const showAdd = ref(false);

      const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ["membershipMembers"] });
        queryClient.invalidateQueries({ queryKey: ["membershipLinkedProfiles"] });
        // Refresh the program list so the active-member count stays in sync.
        queryClient.invalidateQueries({ queryKey: ["membershipPrograms"] });
      };

      const membersQuery = useQuery({
        queryKey: ["membershipMembers", props.program.id],
        queryFn: () =>
          (isGroup
            ? trpc.membershipPrograms.groupMembers.query({
                program_id: props.program.id,
              })
            : trpc.membershipPrograms.members.query({
                program_id: props.program.id,
              })) as Promise<MembershipMember[]>,
      });
      const members = computed(() => membersQuery.data.value ?? []);

      const directoryQuery = useQuery({
        queryKey: computed(() => [
          "membershipLinkedProfiles",
          props.program.id,
          search.value,
        ]),
        queryFn: () =>
          (isGroup
            ? trpc.membershipPrograms.groupLinkedProfiles.query({
                program_id: props.program.id,
                search: search.value || null,
              })
            : trpc.membershipPrograms.linkedProfiles.query({
                program_id: props.program.id,
                search: search.value || null,
              })) as Promise<MembershipLinkedProfile[]>,
      });
      const directory = computed(() =>
        (directoryQuery.data.value ?? []).filter((p) => !p.is_member),
      );

      const addMutation = useMutation({
        mutationFn: (input: {
          email: string;
          name?: string | null;
          phone?: string | null;
          birthday?: string | null;
        }) =>
          isGroup
            ? trpc.membershipPrograms.groupAddMember.mutate({
                program_id: props.program.id,
                ...input,
              })
            : trpc.membershipPrograms.addMember.mutate({
                program_id: props.program.id,
                ...input,
              }),
        onSuccess: () => {
          showAdd.value = false;
          invalidate();
        },
      });

      const removeMutation = useMutation({
        mutationFn: (memberId: string) =>
          isGroup
            ? trpc.membershipPrograms.groupRemoveMember.mutate({
                program_id: props.program.id,
                member_id: memberId,
              })
            : trpc.membershipPrograms.removeMember.mutate({
                program_id: props.program.id,
                member_id: memberId,
              }),
        onSuccess: invalidate,
      });

      const formatDate = (value: string | null) =>
        value ? new Date(value).toLocaleDateString() : null;

      const avatar = (
        person: { name?: string | null; email?: string | null },
        tone: "primary" | "muted",
      ) => (
        <div
          class={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
            tone === "primary"
              ? "bg-primary/10 text-primary"
              : "bg-base-200 text-base-content/50"
          }`}
        >
          {initials(person)}
        </div>
      );

      const memberRow = (member: MembershipMember) => (
        <div
          key={member.id}
          class="group flex items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-base-200/60"
        >
          {avatar(member, "primary")}
          <div class="min-w-0 flex-1 leading-tight">
            <div class="truncate text-sm text-accent">
              {member.name || member.email}
            </div>
            {(member.name || member.expires_at) && (
              <div class="truncate text-[11px] text-base-content/40">
                {[
                  member.name ? member.email : null,
                  member.expires_at
                    ? t("group.loyalty.expires_on", [
                        formatDate(member.expires_at) ?? "",
                      ])
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            )}
          </div>
          {canManage && (
            <button
              class="btn btn-ghost btn-xs btn-square text-base-content/40 opacity-0 hover:text-error group-hover:opacity-100"
              disabled={removeMutation.isPending.value}
              onClick={() => removeMutation.mutate(member.id)}
            >
              <span class="material-symbols-outlined text-[18px]">close</span>
            </button>
          )}
        </div>
      );

      const directoryRow = (profile: MembershipLinkedProfile) => (
        <div
          key={profile.profile_id}
          class="group flex items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-base-200/60"
        >
          {avatar(profile, "muted")}
          <div class="min-w-0 flex-1 leading-tight">
            <div class="truncate text-sm text-accent">
              {profile.name || profile.email}
            </div>
            {profile.name && (
              <div class="truncate text-[11px] text-base-content/40">
                {profile.email}
              </div>
            )}
          </div>
          {canManage && (
            <button
              class="btn btn-ghost btn-xs btn-square text-primary opacity-60 group-hover:opacity-100"
              disabled={addMutation.isPending.value}
              onClick={() =>
                addMutation.mutate({
                  email: profile.email ?? "",
                  name: profile.name,
                })
              }
            >
              <span class="material-symbols-outlined text-[18px]">add</span>
            </button>
          )}
        </div>
      );

      const sectionLabel = (text: string, count?: number) => (
        <div class="flex items-center gap-2 px-1 text-[11px] font-semibold uppercase tracking-wide text-base-content/40">
          {text}
          {count !== undefined && (
            <span class="text-base-content/30">{count}</span>
          )}
        </div>
      );

      return () => (
        <>
        <RightSheet
          isOpen
          onClose={props.onClose}
          width="440px"
          title={
            <div class="flex min-w-0 items-center gap-2">
              <span class="material-symbols-outlined text-primary">group</span>
              <div class="min-w-0">
                <div class="truncate text-base font-semibold text-accent">
                  {props.program.name}
                </div>
                <div class="text-[11px] text-base-content/40">
                  {t("group.loyalty.manage_members")}
                </div>
              </div>
            </div>
          }
        >
          <div class="flex min-h-0 flex-1 flex-col overflow-hidden">
            {canManage && (
              <div class="shrink-0 border-b border-base-200 p-3">
                <button
                  class="btn btn-primary btn-sm w-full gap-1"
                  onClick={() => {
                    addMutation.reset();
                    showAdd.value = true;
                  }}
                >
                  <span class="material-symbols-outlined text-[18px]">add</span>
                  {t("group.loyalty.add_member")}
                </button>
              </div>
            )}

            <div class="min-h-0 flex-1 space-y-4 overflow-y-auto p-3">
              <section class="space-y-1">
                {sectionLabel(
                  t("group.loyalty.members"),
                  members.value.length,
                )}
                {membersQuery.isLoading.value ? (
                  <div class="py-5 text-center">
                    <span class="loading loading-spinner loading-sm text-base-content/30" />
                  </div>
                ) : members.value.length === 0 ? (
                  <div class="px-1 py-5 text-center text-xs text-base-content/40">
                    {t("group.loyalty.no_members")}
                  </div>
                ) : (
                  <div>{members.value.map(memberRow)}</div>
                )}
              </section>

              <section class="space-y-1.5">
                {sectionLabel(
                  isGroup
                    ? t("group.loyalty.directory_group")
                    : t("group.loyalty.directory_shop"),
                )}
                <label class="input input-bordered input-sm flex items-center gap-2 focus-within:input-primary">
                  <span class="material-symbols-outlined text-[18px] text-base-content/40">
                    search
                  </span>
                  <input
                    class="min-w-0 grow"
                    placeholder={t("group.loyalty.search_customers")}
                    value={search.value}
                    onInput={(e) =>
                      (search.value = (e.target as HTMLInputElement).value)
                    }
                  />
                </label>
                {directoryQuery.isLoading.value ? (
                  <div class="py-5 text-center">
                    <span class="loading loading-spinner loading-sm text-base-content/30" />
                  </div>
                ) : directory.value.length === 0 ? (
                  <div class="px-1 py-5 text-center text-xs text-base-content/40">
                    {t("group.loyalty.no_customers")}
                  </div>
                ) : (
                  <div>{directory.value.map(directoryRow)}</div>
                )}
              </section>
            </div>
          </div>
        </RightSheet>
        {showAdd.value && (
          <AddMemberModal
            onClose={() => (showAdd.value = false)}
            onSubmit={(input: AddMemberInput) => addMutation.mutate(input)}
            pending={addMutation.isPending.value}
            error={
              addMutation.error.value
                ? (addMutation.error.value as Error).message
                : null
            }
          />
        )}
        </>
      );
    },
  });
